import { AxiosResponse } from "axios";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { ApiError } from "../utils/apiClients/ApiError";
import { BaseAnalysisEngine } from "./baseAnalysisEngine";
import type { paths, components } from "../types/voislog_api";
import {
  PathsWithMethod,
  SuccessResponse,
  ResponseObjectMap,
  MediaType,
} from "openapi-typescript-helpers";

type EngineType = components["schemas"]["EngineType"];
type AnalysisPath<T extends EngineType> =
  `/v2/core/analysis/${T}/recordings/{recording_id}`;

// POST を持つエンジンのみに絞る
export type AnalyzableEngineType = {
  [T in EngineType]: AnalysisPath<T> extends PathsWithMethod<paths, "post">
    ? T
    : never;
}[EngineType];

// /recordings/{recording_id} のGETレスポンス型
export type RecordingResponse =
  paths["/v2/core/recordings/{recording_id}"]["get"]["responses"][200]["content"]["application/json"];

type AnalysisGetOperation<T extends EngineType> = paths[AnalysisPath<T>]["get"];
export type AnalysisResult<T extends EngineType> = SuccessResponse<
  ResponseObjectMap<AnalysisGetOperation<T>>,
  MediaType
>;

type ApiGet<T extends EngineType> = (
  url: AnalysisPath<T>,
  init: { params: { path: { recording_id: string } } },
) => Promise<{ data?: AnalysisResult<T>; error?: unknown; response: Response }>;

type ApiPost<T extends AnalyzableEngineType> = (
  url: AnalysisPath<T>,
  init: {
    params: { path: { recording_id: string } };
    body?: { option: { detect_volume: number } };
  },
) => Promise<{ data?: AnalysisResult<T>; error?: unknown; response: Response }>;

// voislogApiを使用するエンジン（mi1, mi2, mi2.1, co1, co2, co2.1, sl1）はこちらを継承する
export abstract class CoreAnalysisEngine<
  E extends AnalyzableEngineType,
> extends BaseAnalysisEngine<AnalysisResult<E>> {
  _resultCaches: Record<string, AnalysisResult<E>> = {};
  private _recordingCaches: Record<string, RecordingResponse> = {};

  private readonly analysisPath: AnalysisPath<E>;

  constructor(engineType: E) {
    super();
    this.analysisPath = `/v2/core/analysis/${engineType}/recordings/{recording_id}`;
  }

  async getResult(fileId: string): Promise<AnalysisResult<E> | null> {
    if (this._resultCaches[fileId]) {
      return this._resultCaches[fileId];
    }
    if (!this.isContracted()) {
      return null;
    }

    try {
      const { data, error, response } = await (voislogApi.GET as ApiGet<E>)(
        this.analysisPath,
        { params: { path: { recording_id: fileId } } },
      );
      if (error) throw new ApiError(response, "get", error);
      this._resultCaches[fileId] = data as AnalysisResult<E>;
      return data as AnalysisResult<E>;
    } catch {
      return null;
    }
  }

  async getRecording(fileId: string): Promise<RecordingResponse | null> {
    if (this._recordingCaches[fileId]) {
      return this._recordingCaches[fileId];
    }
    try {
      const { data, error, response } = await voislogApi.GET(
        "/v2/core/recordings/{recording_id}",
        { params: { path: { recording_id: fileId } } },
      );
      if (error) throw new ApiError(response, "get", error);
      this._recordingCaches[fileId] = data;
      return data;
    } catch {
      return null;
    }
  }

  async preActivate(): Promise<void> {
    // CoreAPIにはpre_activateエンドポイントが存在しないため何もしない
  }

  /**
   * @throws {Error}
   */
  async analyze(
    fileId: string,
    detectVolume: number,
  ): Promise<AxiosResponse<AnalysisResult<E>>> {
    if (!this.isContracted()) {
      throw new Error(
        `Engine ${this._engineType} is not contracted. Cannot analyze.`,
      );
    }

    const startTime = performance.now();
    const { data, error, response } = await (voislogApi.POST as ApiPost<E>)(
      this.analysisPath,
      {
        params: { path: { recording_id: fileId } },
        body: {
          option: { detect_volume: Math.round(detectVolume) },
        },
      },
    );
    if (error) throw new ApiError(response, "post", error);
    const endTime = performance.now();
    console.info(
      "[CoreAPI] " +
        this.analysisPath +
        " : " +
        (endTime - startTime).toFixed(3) +
        "ms",
    );
    this._resultCaches[fileId] = data as AnalysisResult<E>;
    // AxiosResponse互換のオブジェクトを返す
    return {
      data: data as AnalysisResult<E>,
      status: 200,
      statusText: "OK",
      headers: {},
      config: {},
    } as AxiosResponse<AnalysisResult<E>>;
  }
}
